'use client';

import { motion } from 'framer-motion';
import { FileSearch, Globe, Droplet, LineChart, Landmark } from 'lucide-react';
import { cn } from '@/lib/cn';

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  className?: string;
  children: React.ReactNode;
  delay?: number;
}

function FeatureCard({ title, description, icon, className, children, delay = 0 }: FeatureCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -3 }}
      className={cn(
        'rounded-2xl p-6 border border-[var(--color-border-subtle)] overflow-hidden flex flex-col',
        className
      )}
      style={{ backgroundColor: 'var(--color-surface)' }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
          style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)', color: 'var(--color-accent-muted)' }}
        >
          {icon}
        </div>
        <h3 className="text-lg font-semibold text-[var(--color-text)]">{title}</h3>
      </div>
      <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--color-text-secondary)' }}>{description}</p>
      <div className="mt-auto">{children}</div>
    </motion.div>
  );
}

function ResearchMockup() {
  const stats = [
    { l: 'P/E', v: '28.4' },
    { l: 'EPS', v: '6.43' },
    { l: '52W H', v: '199.62' },
    { l: '52W L', v: '124.17' },
    { l: 'MKT CAP', v: '2.86T' },
    { l: 'BETA', v: '1.34' },
  ];
  return (
    <div className="rounded-lg p-3 font-mono" style={{ backgroundColor: '#0A0E17', border: '1px solid #1F2937' }}>
      {/* Header: ticker + price */}
      <div className="flex items-center gap-2 pb-2 mb-2" style={{ borderBottom: '1px solid #1F2937' }}>
        <span style={{ color: '#22D3EE', fontWeight: 700, fontSize: 12 }}>AAPL</span>
        <span style={{ color: '#6B7280', fontSize: 9 }}>NASDAQ</span>
        <span className="ml-auto tabular-nums" style={{ color: '#E5E7EB', fontWeight: 700, fontSize: 12 }}>$187.43</span>
        <span className="tabular-nums" style={{ color: '#10B981', fontSize: 10 }}>+1.32%</span>
      </div>
      {/* Tabs */}
      <div className="flex items-center gap-3 pb-2 mb-2 text-[9px] uppercase tracking-wider" style={{ borderBottom: '1px solid #1F2937' }}>
        <span style={{ color: '#22D3EE', fontWeight: 700 }}>Overview</span>
        <span style={{ color: '#6B7280' }}>Financials</span>
        <span style={{ color: '#6B7280' }}>Risk</span>
      </div>
      {/* Stat grid */}
      <div className="grid grid-cols-3 gap-2">
        {stats.map((s) => (
          <div key={s.l} className="rounded p-1.5" style={{ backgroundColor: '#111827' }}>
            <div className="text-[8px] uppercase tracking-wider" style={{ color: '#6B7280' }}>{s.l}</div>
            <div className="tabular-nums text-[11px] font-bold" style={{ color: '#E5E7EB' }}>{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function IndexDrilldownMockup() {
  const rows = [
    { t: 'AAPL', n: 'Apple Inc.', w: 7.12, c: 1.32 },
    { t: 'MSFT', n: 'Microsoft Corp.', w: 6.48, c: 0.84 },
    { t: 'NVDA', n: 'NVIDIA Corp.', w: 5.91, c: 2.34 },
    { t: 'AMZN', n: 'Amazon.com', w: 3.42, c: -0.42 },
    { t: 'GOOGL', n: 'Alphabet Inc.', w: 2.17, c: 0.68 },
  ];
  return (
    <div className="rounded-lg p-3 font-mono text-[10px]" style={{ backgroundColor: '#0A0E17', border: '1px solid #1F2937' }}>
      <div className="flex items-center justify-between pb-2 mb-2" style={{ borderBottom: '1px solid #1F2937' }}>
        <span style={{ color: '#22D3EE', fontWeight: 700 }}>S&P 500</span>
        <span style={{ color: '#6B7280', fontSize: 9 }}>CONSTITUENTS · 503</span>
      </div>
      <div className="grid grid-cols-12 gap-2 pb-1.5 mb-1" style={{ color: '#6B7280', fontSize: 9 }}>
        <span className="col-span-2">SYM</span>
        <span className="col-span-6">NAME</span>
        <span className="col-span-2 text-right">WEIGHT</span>
        <span className="col-span-2 text-right">CHG</span>
      </div>
      {rows.map((r) => {
        const positive = r.c >= 0;
        return (
          <div key={r.t} className="grid grid-cols-12 gap-2 py-1 items-center">
            <span className="col-span-2" style={{ color: '#22D3EE', fontWeight: 600 }}>{r.t}</span>
            <span className="col-span-6 truncate" style={{ color: '#E5E7EB' }}>{r.n}</span>
            <span className="col-span-2 text-right tabular-nums" style={{ color: '#E5E7EB' }}>{r.w.toFixed(2)}%</span>
            <span className="col-span-2 text-right tabular-nums" style={{ color: positive ? '#10B981' : '#EF4444' }}>
              {positive ? '+' : ''}{r.c.toFixed(2)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

function CommoditiesMockup() {
  const items = [
    { l: 'Crude Oil (WTI)', s: 'CL=F', p: 78.42, c: 1.24, color: '#F59E0B', spark: 'M0,28 L20,30 L40,26 L60,24 L80,20 L100,22 L120,18 L140,14 L160,16 L180,12 L200,10' },
    { l: 'Gold', s: 'GC=F', p: 2418.30, c: 0.42, color: '#FBBF24', spark: 'M0,22 L20,20 L40,18 L60,16 L80,18 L100,14 L120,12 L140,10 L160,12 L180,8 L200,6' },
    { l: 'Silver', s: 'SI=F', p: 31.18, c: -0.68, color: '#9CA3AF', spark: 'M0,12 L20,14 L40,16 L60,14 L80,18 L100,20 L120,22 L140,20 L160,24 L180,26 L200,28' },
  ];
  return (
    <div className="rounded-lg p-3 font-mono" style={{ backgroundColor: '#0A0E17', border: '1px solid #1F2937' }}>
      <div className="flex items-center justify-between pb-2 mb-2" style={{ borderBottom: '1px solid #1F2937' }}>
        <span style={{ color: '#22D3EE', fontWeight: 700, fontSize: 10 }}>COMMODITIES</span>
        <span style={{ color: '#6B7280', fontSize: 9 }}>LIVE</span>
      </div>
      <div className="space-y-2">
        {items.map((c) => {
          const positive = c.c >= 0;
          return (
            <div key={c.s} className="flex items-center gap-2">
              <div className="flex-shrink-0" style={{ minWidth: 90 }}>
                <div className="text-[10px]" style={{ color: '#E5E7EB' }}>{c.l}</div>
                <div className="text-[8px]" style={{ color: '#6B7280' }}>{c.s}</div>
              </div>
              <svg viewBox="0 0 200 36" className="flex-1 h-7" preserveAspectRatio="none">
                <path d={c.spark} fill="none" stroke={c.color} strokeWidth="1.5" />
              </svg>
              <div className="flex-shrink-0 text-right" style={{ minWidth: 64 }}>
                <div className="text-[10px] tabular-nums" style={{ color: '#E5E7EB', fontWeight: 700 }}>{c.p.toFixed(2)}</div>
                <div className="text-[9px] tabular-nums" style={{ color: positive ? '#10B981' : '#EF4444' }}>
                  {positive ? '+' : ''}{c.c.toFixed(2)}%
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VixSpyMockup() {
  return (
    <div className="rounded-lg p-3" style={{ backgroundColor: '#0A0E17', border: '1px solid #1F2937' }}>
      <div className="flex items-center justify-between pb-2 mb-2" style={{ borderBottom: '1px solid #1F2937' }}>
        <span className="font-mono text-[10px]" style={{ color: '#22D3EE', fontWeight: 700 }}>SPX vs VIX</span>
        <div className="flex items-center gap-3 font-mono text-[9px]">
          <span style={{ color: '#10B981' }}>● SPX</span>
          <span style={{ color: '#F59E0B' }}>● VIX</span>
        </div>
      </div>
      <svg viewBox="0 0 240 80" className="w-full h-20" preserveAspectRatio="none">
        <defs>
          <linearGradient id="spxFill2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* SPX (rising) */}
        <path
          d="M0,55 L20,52 L40,48 L60,50 L80,42 L100,45 L120,38 L140,30 L160,32 L180,24 L200,20 L220,18 L240,12 L240,80 L0,80 Z"
          fill="url(#spxFill2)"
        />
        <path
          d="M0,55 L20,52 L40,48 L60,50 L80,42 L100,45 L120,38 L140,30 L160,32 L180,24 L200,20 L220,18 L240,12"
          fill="none" stroke="#10B981" strokeWidth="1.5"
        />
        {/* VIX (falling, inverse) */}
        <path
          d="M0,30 L20,32 L40,38 L60,42 L80,48 L100,50 L120,54 L140,58 L160,62 L180,64 L200,66 L220,68 L240,70"
          fill="none" stroke="#F59E0B" strokeWidth="1.5"
        />
      </svg>
      <div className="mt-2 text-[9px] font-mono" style={{ color: '#6B7280' }}>
        Volatility falls as the market rallies — the classic inverse relationship.
      </div>
    </div>
  );
}

function MacroMockup() {
  const yields = [
    { term: '2Y', y: 4.82 },
    { term: '5Y', y: 4.34 },
    { term: '10Y', y: 4.21 },
    { term: '30Y', y: 4.48 },
  ];
  const stats = [
    { l: 'FED FUNDS', v: '5.33%' },
    { l: 'CPI YoY', v: '3.2%' },
    { l: 'UNEMPL.', v: '4.1%' },
    { l: 'GDP', v: '2.8%' },
  ];
  // Map yield to y-coord (0-40 svg)
  const minY = 4.0, maxY = 5.0;
  const points = yields.map((p, i) => {
    const x = (i / (yields.length - 1)) * 200;
    const y = 40 - ((p.y - minY) / (maxY - minY)) * 32 - 4;
    return { ...p, x, y };
  });
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  return (
    <div className="rounded-lg p-3 font-mono" style={{ backgroundColor: '#0A0E17', border: '1px solid #1F2937' }}>
      <div className="flex items-center justify-between pb-2 mb-2" style={{ borderBottom: '1px solid #1F2937' }}>
        <span style={{ color: '#22D3EE', fontWeight: 700, fontSize: 10 }}>TREASURY YIELD CURVE</span>
      </div>
      {/* Yield curve */}
      <div className="relative">
        <svg viewBox="0 0 200 44" className="w-full h-14" preserveAspectRatio="none">
          <path d={path} fill="none" stroke="#3B82F6" strokeWidth="1.5" />
          {points.map((p) => (
            <circle key={p.term} cx={p.x} cy={p.y} r="2" fill="#3B82F6" />
          ))}
        </svg>
        <div className="flex justify-between mt-1 text-[8px]" style={{ color: '#6B7280' }}>
          {yields.map((p) => (
            <span key={p.term}>{p.term} {p.y.toFixed(2)}%</span>
          ))}
        </div>
      </div>
      {/* Macro stats */}
      <div className="grid grid-cols-4 gap-1.5 mt-3 pt-2" style={{ borderTop: '1px solid #1F2937' }}>
        {stats.map((s) => (
          <div key={s.l}>
            <div className="text-[8px] uppercase tracking-wider" style={{ color: '#6B7280' }}>{s.l}</div>
            <div className="tabular-nums text-[10px] font-bold" style={{ color: '#E5E7EB' }}>{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FeaturesGrid() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto text-center mb-12"
        >
          <span
            className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: 'var(--color-accent-muted)' }}
          >
            Features
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)]">
            Everything you need on one screen
          </h2>
          <p className="mt-3 text-lg" style={{ color: 'var(--color-text-secondary)' }}>
            Designed for keyboard-driven workflows and dense, glanceable data.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 max-w-6xl mx-auto">
          {/* Row 1: full-width Research feature */}
          <FeatureCard
            title="Stock research mode"
            description="Open any ticker into a focused research view: P/E, EPS, 52-week highs and lows on the Overview tab; revenue, profitability, balance sheet, and cash flow on Financials; Beta, short interest, and 52-week range on Risk."
            icon={<FileSearch size={20} />}
            className="md:col-span-6"
            delay={0}
          >
            <ResearchMockup />
          </FeatureCard>

          {/* Row 2: index drilldown + commodities */}
          <FeatureCard
            title="Market data — what's inside the index"
            description="Tap S&P 500, Dow Jones, NASDAQ, or Russell to see every constituent with its weight and today's move. Click a row to jump straight into Research."
            icon={<Globe size={20} />}
            className="md:col-span-3"
            delay={0.05}
          >
            <IndexDrilldownMockup />
          </FeatureCard>

          <FeatureCard
            title="Commodities at a glance"
            description="Crude oil (WTI), gold, and silver — price, change, and a sparkline so you can read the macro tape without leaving the terminal."
            icon={<Droplet size={20} />}
            className="md:col-span-3"
            delay={0.1}
          >
            <CommoditiesMockup />
          </FeatureCard>

          {/* Row 3: VIX vs SPY + macro */}
          <FeatureCard
            title="VIX vs SPY"
            description="A dedicated dual-line chart that plots the S&P 500 against the volatility index, with an explanation of the inverse relationship."
            icon={<LineChart size={20} />}
            className="md:col-span-3"
            delay={0.15}
          >
            <VixSpyMockup />
          </FeatureCard>

          <FeatureCard
            title="Macro economics"
            description="The 2 / 5 / 10 / 30-year Treasury yield curve plus the Fed funds rate, CPI, unemployment, and GDP growth — the backdrop every position is priced against."
            icon={<Landmark size={20} />}
            className="md:col-span-3"
            delay={0.2}
          >
            <MacroMockup />
          </FeatureCard>
        </div>
      </div>
    </section>
  );
}
